/**
 * Minimal async key/value storage on IndexedDB for the TanStack Query persister (T4 §2.2: queries persisted to
 * IndexedDB so the wallet opens instantly and offline). Falls back to doing nothing when IndexedDB is unavailable.
 */
const DB = 'iris';
const STORE = 'kv';

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function run<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | undefined> {
  try {
    const db = await open();
    return await new Promise<T>((resolve, reject) => {
      const request = work(db.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch {
    return undefined;
  }
}

export const idbStorage = {
  getItem: async (key: string) => (await run<string | undefined>('readonly', (s) => s.get(key))) ?? null,
  setItem: async (key: string, value: string) => {
    await run('readwrite', (s) => s.put(value, key));
  },
  removeItem: async (key: string) => {
    await run('readwrite', (s) => s.delete(key));
  },
};
